import { BadgeCheck, LoaderCircle, Play, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import "../styles/views/home-view.css";
import MusicCard from "../components/cards/MusicCard";
import UserCard from "../components/cards/UserCard";
import PlaylistCard from "../components/cards/PlaylistCard";
import AlbumCard from "../components/cards/AlbumCard";
import TrackList from "../components/lists/TrackList";
import {
  getPlaylistDetailResolved,
  getTrackDetail,
  getUserAlbums,
  getUserDetail,
  getUserPlaylists,
  getUserTracks,
} from "../services/soundcloud/api";
import { mapAlbumToCard, mapPlaylistToCard, mapTrackToCard } from "../services/soundcloud/mappers";

function valueOrNA(value) {
  if (value === undefined || value === null || value === "") return "N/A";
  return String(value);
}

function extractId(entity) {
  if (!entity) return 0;
  if (typeof entity.id === "number") return entity.id;
  if (typeof entity?.model?.id === "number") return entity.model.id;
  if (typeof entity.id === "string") {
    const match = entity.id.match(/(\d+)$/);
    if (match) return Number(match[1]);
  }
  return 0;
}

function normalizeCollection(payload) {
  if (Array.isArray(payload?.collection)) return payload.collection;
  if (Array.isArray(payload)) return payload;
  return [];
}

function getTrackAvailability(trackLike = {}) {
  if (trackLike?.isUnavailable) {
    return {
      isUnavailable: true,
      text: trackLike?.unavailableText || "Not available in your country",
    };
  }

  const raw = trackLike?.model || trackLike;
  const transcodings = Array.isArray(raw?.media?.transcodings) ? raw.media.transcodings : [];
  const hasTranscodings = transcodings.length > 0;
  const allSnipped = hasTranscodings && transcodings.every((item) => Boolean(item?.snipped));
  const policy = String(raw?.policy || "").toLowerCase();
  const policyRestricted = policy.includes("snip") || policy.includes("blocked");
  const isUnavailable = allSnipped || (policyRestricted && hasTranscodings);

  return {
    isUnavailable,
    text: isUnavailable ? "Not available in your country" : "",
  };
}

function formatCount(value) {
  if (value === undefined || value === null || value === "") return "N/A";
  const count = Number(value);
  if (!Number.isFinite(count)) return "N/A";
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(Math.floor(count));
}

function formatDurationValue(value) {
  if (value === undefined || value === null || value === "") return "N/A";
  if (typeof value === "string" && value.includes(":")) return value;
  const durationMs = Number(value);
  if (!Number.isFinite(durationMs)) return "N/A";
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function HomeView({
  heroAlbum,
  recommendedCards,
  relatedTracks,
  searchCollections,
  onPlayTrack,
  searchQuery = "",
  isLoading = false,
  isLoadingMore = false,
  error = "",
  onRetry,
  onLoadMoreSearch,
  hasMoreSearch = false,
  totalResults = 0,
  totalUsers = 0,
  totalPlaylists = 0,
  totalAlbums = 0,
  hasMoreUsers = false,
  hasMorePlaylists = false,
  hasMoreAlbums = false,
  onLoadMoreUsers,
  onLoadMorePlaylists,
  onLoadMoreAlbums,
  loadingCollection = "",
}) {
  const [heroImageFailed, setHeroImageFailed] = useState(false);
  const [searchTab, setSearchTab] = useState("tracks");
  const [requestedTabs, setRequestedTabs] = useState({
    users: false,
    playlists: false,
    albums: false,
  });
  const [userDetailTab, setUserDetailTab] = useState("info");
  const [detailDialog, setDetailDialog] = useState({
    type: "",
    status: "idle",
    data: null,
    error: "",
  });

  useEffect(() => {
    setHeroImageFailed(false);
  }, [heroAlbum?.image, heroAlbum?.title]);

  useEffect(() => {
    setSearchTab("tracks");
    setRequestedTabs({
      users: false,
      playlists: false,
      albums: false,
    });
    setUserDetailTab("info");
    setDetailDialog({ type: "", status: "idle", data: null, error: "" });
  }, [searchQuery]);

  useEffect(() => {
    if (!searchQuery) return;
    if (loadingCollection) return;
    if (searchTab === "tracks") return;

    if (searchTab === "users" && !requestedTabs.users) {
      setRequestedTabs((prev) => ({ ...prev, users: true }));
      onLoadMoreUsers?.();
      return;
    }

    if (searchTab === "playlists" && !requestedTabs.playlists) {
      setRequestedTabs((prev) => ({ ...prev, playlists: true }));
      onLoadMorePlaylists?.();
      return;
    }

    if (searchTab === "albums" && !requestedTabs.albums) {
      setRequestedTabs((prev) => ({ ...prev, albums: true }));
      onLoadMoreAlbums?.();
    }
  }, [
    loadingCollection,
    onLoadMoreAlbums,
    onLoadMorePlaylists,
    onLoadMoreUsers,
    requestedTabs.albums,
    requestedTabs.playlists,
    requestedTabs.users,
    searchQuery,
    searchTab,
  ]);

  useEffect(() => {
    if (!detailDialog.type) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeDetailDialog();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [detailDialog.type]);

  const users = searchCollections?.users || [];
  const playlists = searchCollections?.playlists || [];
  const albums = searchCollections?.albums || [];

  const hasNoResults =
    !isLoading &&
    searchQuery &&
    recommendedCards.length === 0 &&
    users.length === 0 &&
    playlists.length === 0 &&
    albums.length === 0;

  const searchTabItems = [
    { id: "tracks", label: "Tracks" },
    { id: "users", label: "Users" },
    { id: "playlists", label: "Playlists" },
    { id: "albums", label: "Albums" },
  ];

  const showTracks = searchTab === "tracks";
  const showUsers = searchTab === "users";
  const showPlaylists = searchTab === "playlists";
  const showAlbums = searchTab === "albums";

  const activeDisplayedCount = showTracks
    ? recommendedCards.length
    : showUsers
      ? users.length
      : showPlaylists
        ? playlists.length
        : albums.length;

  const activeTotalCount = showTracks
    ? Number(totalResults) || 0
    : showUsers
      ? Number(totalUsers) || 0
      : showPlaylists
        ? Number(totalPlaylists) || 0
        : Number(totalAlbums) || 0;

  const activeLabel = showTracks
    ? "tracks"
    : showUsers
      ? "users"
      : showPlaylists
        ? "playlists"
        : "albums";

  const dialogTitle = useMemo(() => {
    if (!detailDialog.data) return "Details";
    if (detailDialog.type === "user") return valueOrNA(detailDialog.data.profile?.username);
    if (detailDialog.type === "track") return valueOrNA(detailDialog.data.title);
    if (detailDialog.type === "playlist" || detailDialog.type === "album") {
      return valueOrNA(detailDialog.data.playlist?.title);
    }
    return "Details";
  }, [detailDialog.data, detailDialog.type]);

  function renderEmptyState(entityLabel) {
    return (
      <div className="user-empty-state glass-panel">
        <Play size={16} />
        <p>No {entityLabel} available</p>
        <span>This user has not published any {entityLabel} yet.</span>
      </div>
    );
  }

  function closeDetailDialog() {
    setUserDetailTab("info");
    setDetailDialog({ type: "", status: "idle", data: null, error: "" });
  }

  function handlePlayAlbumClick() {
    const albumTracks = Array.isArray(heroAlbum?.model?.tracks)
      ? heroAlbum.model.tracks.map(mapTrackToCard)
      : [];
    const queueSource = albumTracks.length ? albumTracks : recommendedCards;

    if (!queueSource.length) {
      toast.error("No tracks available in this album.");
      return;
    }

    onPlayTrack?.(queueSource[0], queueSource, 0);
  }

  async function openTrackDetails(trackLike) {
    const id = extractId(trackLike);
    if (!id) return;

    setDetailDialog({ type: "track", status: "loading", data: null, error: "" });
    try {
      const track = await getTrackDetail(id);
      setDetailDialog({ type: "track", status: "ready", data: track, error: "" });
    } catch (fetchError) {
      setDetailDialog({
        type: "track",
        status: "error",
        data: null,
        error: fetchError?.message || "Unable to load track details.",
      });
    }
  }

  async function openPlaylistOrAlbumDetails(entity, type = "playlist") {
    const id = extractId(entity);
    if (!id) return;

    setDetailDialog({ type, status: "loading", data: null, error: "" });
    try {
      const playlist = await getPlaylistDetailResolved(id);
      setDetailDialog({
        type,
        status: "ready",
        data: { playlist },
        error: "",
      });
    } catch (fetchError) {
      setDetailDialog({
        type,
        status: "error",
        data: null,
        error: fetchError?.message || "Unable to load playlist or album details.",
      });
    }
  }

  async function openUserDetails(userModel) {
    const userId = extractId({ id: userModel?.id });
    if (!userId) return;

    setUserDetailTab("info");
    setDetailDialog({ type: "user", status: "loading", data: null, error: "" });

    try {
      const [profile, tracksResponse, playlistsResponse, albumsResponse] = await Promise.all([
        getUserDetail(userId),
        getUserTracks(userId, { limit: 8 }),
        getUserPlaylists(userId, { limit: 8 }),
        getUserAlbums(userId, { limit: 8 }),
      ]);

      const tracks = normalizeCollection(tracksResponse).map(mapTrackToCard);
      const userPlaylists = normalizeCollection(playlistsResponse).map(mapPlaylistToCard);
      const userAlbums = normalizeCollection(albumsResponse).map(mapAlbumToCard);

      setDetailDialog({
        type: "user",
        status: "ready",
        data: {
          profile,
          tracks,
          playlists: userPlaylists,
          albums: userAlbums,
        },
        error: "",
      });
    } catch (fetchError) {
      setDetailDialog({
        type: "user",
        status: "error",
        data: null,
        error: fetchError?.message || "Unable to load user details.",
      });
    }
  }

  if (error) {
    return (
      <section className="glass-panel panel-block home-state">
        <h3 className="section-title">Unable to load Home</h3>
        <p>{error}</p>
        <button type="button" className="hero-play-btn" onClick={onRetry}>
          Retry
        </button>
      </section>
    );
  }

  if (!heroAlbum && isLoading) {
    return (
      <>
        <section className="hero glass-panel hero-skeleton">
          <div className="hero-skeleton-left">
            <span className="shimmer-block hero-skeleton-line sm" />
            <span className="shimmer-block hero-skeleton-line lg" />
            <span className="shimmer-block hero-skeleton-line md" />
            <span className="shimmer-block hero-skeleton-line md" />
            <div className="hero-skeleton-meta">
              <span className="shimmer-block hero-skeleton-pill" />
              <span className="shimmer-block hero-skeleton-pill" />
            </div>
            <span className="shimmer-block hero-skeleton-btn" />
          </div>
          <div className="shimmer-block hero-skeleton-image" />
        </section>

        <section>
          <div className="section-inline">
            <h3 className="section-title">Recommended for You</h3>
            <p className="section-subtle">Loading home feed...</p>
          </div>
          <div className="card-grid">
            {new Array(10).fill(null).map((_, index) => (
              <article key={`home-card-skeleton-${index}`} className="music-card glass-panel card-skeleton" />
            ))}
          </div>
        </section>

        <section className="glass-panel panel-block">
          <h3 className="section-title">Related Tracks</h3>
          <div className="track-list">
            {new Array(5).fill(null).map((_, index) => (
              <div key={`home-track-skeleton-${index}`} className="track-row">
                <div className="track-meta">
                  <span className="track-index">{index + 1}</span>
                  <div className="home-track-skeleton-lines">
                    <span className="shimmer-block home-track-skeleton-line md" />
                    <span className="shimmer-block home-track-skeleton-line sm" />
                  </div>
                </div>
                <span className="shimmer-block home-track-skeleton-line sm" />
                <span className="shimmer-block home-track-skeleton-line xs" />
              </div>
            ))}
          </div>
        </section>
      </>
    );
  }

  if (!heroAlbum) {
    return (
      <section className="glass-panel panel-block home-state">
        <h3 className="section-title">Loading home feed...</h3>
      </section>
    );
  }

  return (
    <>
      <section className="hero glass-panel">
        <div>
          <p className="hero-tag">Trending Galaxy Album</p>
          <h2>{heroAlbum.title}</h2>
          <p>{heroAlbum.subtitle}</p>

          <div className="hero-meta">
            <span>{heroAlbum.listeners}</span>
            <span>{heroAlbum.duration}</span>
          </div>

          <button type="button" className="hero-play-btn" onClick={handlePlayAlbumClick}>
            <Play size={16} />
            Play Album
          </button>
        </div>

        {heroAlbum.image && !heroImageFailed ? (
          <img
            src={heroAlbum.image}
            alt={heroAlbum.title}
            onError={() => setHeroImageFailed(true)}
          />
        ) : (
          <div className="hero-image-fallback" aria-label="No artwork available">
            <Play size={20} />
            <span>No artwork available</span>
          </div>
        )}
      </section>

      <section>
        <div className={`section-inline${searchQuery ? " search-results-header" : ""}`}>
          <h3 className="section-title">
            {searchQuery ? `Search Results for "${searchQuery}"` : "Recommended for You"}
          </h3>
          <p className="section-subtle">
            {activeTotalCount > 0
              ? `Showing ${activeDisplayedCount} / ${activeTotalCount} ${activeLabel}`
              : `Loaded ${activeDisplayedCount} ${activeLabel}`}
          </p>
        </div>

        {searchQuery ? (
          <div className="search-tabs-wrap">
            <p className="search-tabs-label">Filter results</p>
            <div className="search-tabs" role="tablist" aria-label="Search result categories">
              {searchTabItems.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={searchTab === tab.id}
                  className={`search-tab-btn${searchTab === tab.id ? " is-active" : ""}`}
                  onClick={() => setSearchTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {hasNoResults ? (
          <div className="glass-panel panel-block home-state">
            <p>No matching tracks found. Try another keyword.</p>
          </div>
        ) : showTracks ? (
          <>
            <div className="card-grid">
              {(isLoading ? new Array(15).fill(null) : recommendedCards).map((card, index) =>
                card ? (
                  <MusicCard
                    key={card.id}
                    {...card}
                    onOpenDetails={() => openTrackDetails(card)}
                    onPlay={() => onPlayTrack?.(card, recommendedCards, index)}
                  />
                ) : (
                  <article
                    key={`skeleton-${index}`}
                    className="music-card glass-panel card-skeleton"
                  />
                )
              )}
            </div>
            <div className="section-actions">
              {hasMoreSearch ? (
                <button
                  type="button"
                  className="section-link-btn"
                  onClick={onLoadMoreSearch}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? "Loading..." : "Load more"}
                </button>
              ) : (
                <button type="button" className="section-link-btn" disabled>
                  All loaded
                </button>
              )}
            </div>
          </>
        ) : null}
      </section>

      {!searchQuery ? (
        <section className="glass-panel panel-block">
          <h3 className="section-title">Related Tracks</h3>
          {relatedTracks.length ? (
            <TrackList
              tracks={relatedTracks}
              showPlays
              onOpenDetails={openTrackDetails}
              onPlayTrack={(track, index) => onPlayTrack?.(track, relatedTracks, index)}
            />
          ) : (
            <p className="home-state-inline">Related tracks will appear here.</p>
          )}
        </section>
      ) : (
        <>
          {showUsers ? (
            <section>
              <h3 className="section-title">Users</h3>
              {users.length ? (
                <>
                  <div className="card-grid search-extra-grid">
                    {users.map((item) => (
                      <UserCard key={item.id} {...item} onOpenDetails={openUserDetails} />
                    ))}
                  </div>
                  <div className="section-actions">
                    {hasMoreUsers ? (
                      <button
                        type="button"
                        className="section-link-btn"
                        onClick={onLoadMoreUsers}
                        disabled={isLoadingMore && loadingCollection === "users"}
                      >
                        {isLoadingMore && loadingCollection === "users"
                          ? "Loading..."
                          : "Load more users"}
                      </button>
                    ) : (
                      <button type="button" className="section-link-btn" disabled>
                        All users loaded
                      </button>
                    )}
                  </div>
                </>
              ) : isLoadingMore && loadingCollection === "users" ? (
                <div className="card-grid search-extra-grid">
                  {new Array(8).fill(null).map((_, index) => (
                    <article
                      key={`users-skeleton-${index}`}
                      className="user-card glass-panel card-skeleton user-card-skeleton"
                    />
                  ))}
                </div>
              ) : (
                <p className="home-state-inline">No users found.</p>
              )}
            </section>
          ) : null}

          {showPlaylists ? (
            <section>
              <h3 className="section-title">Playlists</h3>
              {playlists.length ? (
                <>
                  <div className="card-grid search-extra-grid">
                    {playlists.map((item) => (
                      <PlaylistCard
                        key={item.id}
                        {...item}
                        onOpenDetails={() => openPlaylistOrAlbumDetails(item, "playlist")}
                      />
                    ))}
                  </div>
                  <div className="section-actions">
                    {hasMorePlaylists ? (
                      <button
                        type="button"
                        className="section-link-btn"
                        onClick={onLoadMorePlaylists}
                        disabled={isLoadingMore && loadingCollection === "playlists"}
                      >
                        {isLoadingMore && loadingCollection === "playlists"
                          ? "Loading..."
                          : "Load more playlists"}
                      </button>
                    ) : (
                      <button type="button" className="section-link-btn" disabled>
                        All playlists loaded
                      </button>
                    )}
                  </div>
                </>
              ) : isLoadingMore && loadingCollection === "playlists" ? (
                <div className="card-grid search-extra-grid">
                  {new Array(8).fill(null).map((_, index) => (
                    <article
                      key={`playlists-skeleton-${index}`}
                      className="collection-card-skeleton glass-panel card-skeleton"
                    />
                  ))}
                </div>
              ) : (
                <p className="home-state-inline">No playlists found.</p>
              )}
            </section>
          ) : null}

          {showAlbums ? (
            <section>
              <h3 className="section-title">Albums</h3>
              {albums.length ? (
                <>
                  <div className="card-grid search-extra-grid">
                    {albums.map((item) => (
                      <AlbumCard
                        key={item.id}
                        {...item}
                        onOpenDetails={() => openPlaylistOrAlbumDetails(item, "album")}
                      />
                    ))}
                  </div>
                  <div className="section-actions">
                    {hasMoreAlbums ? (
                      <button
                        type="button"
                        className="section-link-btn"
                        onClick={onLoadMoreAlbums}
                        disabled={isLoadingMore && loadingCollection === "albums"}
                      >
                        {isLoadingMore && loadingCollection === "albums"
                          ? "Loading..."
                          : "Load more albums"}
                      </button>
                    ) : (
                      <button type="button" className="section-link-btn" disabled>
                        All albums loaded
                      </button>
                    )}
                  </div>
                </>
              ) : isLoadingMore && loadingCollection === "albums" ? (
                <div className="card-grid search-extra-grid">
                  {new Array(8).fill(null).map((_, index) => (
                    <article
                      key={`albums-skeleton-${index}`}
                      className="collection-card-skeleton glass-panel card-skeleton"
                    />
                  ))}
                </div>
              ) : (
                <p className="home-state-inline">No albums found.</p>
              )}
            </section>
          ) : null}
        </>
      )}

      {detailDialog.type ? (
        <div className="user-detail-overlay" onClick={closeDetailDialog}>
          <article
            className="user-detail-modal glass-panel"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`${dialogTitle} details`}
          >
            <button
              type="button"
              className="user-detail-close"
              onClick={closeDetailDialog}
              aria-label="Close details"
            >
              <X size={18} />
            </button>

            {detailDialog.status === "loading" ? (
              <div className="detail-loading">
                <div className="detail-loading-head">
                  <div className="detail-loading-avatar shimmer-block" />
                  <div className="detail-loading-head-text">
                    <span className="shimmer-block detail-loading-line lg" />
                    <span className="shimmer-block detail-loading-line md" />
                  </div>
                </div>

                <div className="detail-loading-grid">
                  {new Array(6).fill(null).map((_, index) => (
                    <span key={`detail-grid-${index}`} className="shimmer-block detail-loading-pill" />
                  ))}
                </div>

                <div className="detail-loading-list">
                  {new Array(4).fill(null).map((_, index) => (
                    <span key={`detail-list-${index}`} className="shimmer-block detail-loading-row" />
                  ))}
                </div>

                <p className="detail-loading-text">
                  <LoaderCircle size={15} className="spin" />
                  Loading details...
                </p>
              </div>
            ) : null}

            {detailDialog.status === "error" ? (
              <div className="detail-error">
                <p>{detailDialog.error || "Unable to load details."}</p>
              </div>
            ) : null}

            {detailDialog.status === "ready" && detailDialog.type === "user" ? (
              <>
                <div className="user-detail-head">
                  <img
                    src={
                      detailDialog.data.profile?.hdAvatarURL ||
                      detailDialog.data.profile?.avatar_url ||
                      ""
                    }
                    alt={detailDialog.data.profile?.username || "user avatar"}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                  <div>
                    <h3>
                      {valueOrNA(detailDialog.data.profile?.username)}
                      {detailDialog.data.profile?.verified ? (
                        <span className="user-detail-verified" title="Verified artist">
                          <BadgeCheck size={16} />
                        </span>
                      ) : null}
                    </h3>
                    <p>{valueOrNA(detailDialog.data.profile?.full_name)}</p>
                  </div>
                </div>

                <div className="user-detail-tabs" role="tablist" aria-label="User detail tabs">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={userDetailTab === "info"}
                    className={`user-detail-tab-btn${userDetailTab === "info" ? " is-active" : ""}`}
                    onClick={() => setUserDetailTab("info")}
                  >
                    Info
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={userDetailTab === "tracks"}
                    className={`user-detail-tab-btn${userDetailTab === "tracks" ? " is-active" : ""}`}
                    onClick={() => setUserDetailTab("tracks")}
                  >
                    Tracks
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={userDetailTab === "playlists"}
                    className={`user-detail-tab-btn${userDetailTab === "playlists" ? " is-active" : ""}`}
                    onClick={() => setUserDetailTab("playlists")}
                  >
                    Playlists
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={userDetailTab === "albums"}
                    className={`user-detail-tab-btn${userDetailTab === "albums" ? " is-active" : ""}`}
                    onClick={() => setUserDetailTab("albums")}
                  >
                    Albums
                  </button>
                </div>

                {userDetailTab === "info" ? (
                  <>
                    <div className="user-detail-grid">
                      <p><strong>ID:</strong> {valueOrNA(detailDialog.data.profile?.id)}</p>
                      <p><strong>Kind:</strong> {valueOrNA(detailDialog.data.profile?.kind)}</p>
                      <p><strong>Verified:</strong> {detailDialog.data.profile?.verified ? "Yes" : "No"}</p>
                      <p><strong>City:</strong> {valueOrNA(detailDialog.data.profile?.city)}</p>
                      <p><strong>Country:</strong> {valueOrNA(detailDialog.data.profile?.country_code)}</p>
                      <p><strong>Followers:</strong> {formatCount(detailDialog.data.profile?.followers_count)}</p>
                      <p><strong>Followings:</strong> {valueOrNA(detailDialog.data.profile?.followings_count)}</p>
                      <p><strong>Tracks:</strong> {valueOrNA(detailDialog.data.profile?.track_count)}</p>
                      <p><strong>Playlists:</strong> {valueOrNA(detailDialog.data.profile?.playlist_count)}</p>
                    </div>

                    <div className="user-detail-bio">
                      <p className="user-detail-label">Description</p>
                      <p>{valueOrNA(detailDialog.data.profile?.description)}</p>
                    </div>
                  </>
                ) : null}

                {userDetailTab === "tracks" ? (
                  <div className="user-related-sections">
                    <div>
                      <p className="user-detail-label">User Tracks</p>
                      <div className="user-detail-list">
                        {(detailDialog.data.tracks || []).length ? (
                          detailDialog.data.tracks.map((item, index) => {
                            const availability = getTrackAvailability(item);
                            return (
                              <div
                                key={item.id}
                                className={`user-detail-list-item${availability.isUnavailable ? " is-unavailable" : ""}`}
                              >
                                <button
                                  type="button"
                                  className="user-detail-main-btn"
                                  onClick={() => openTrackDetails(item)}
                                >
                                  <span>{valueOrNA(item.title)}</span>
                                  <span className={availability.isUnavailable ? "user-detail-track-warning" : ""}>
                                    {availability.isUnavailable
                                      ? availability.text
                                      : formatDurationValue(item.duration)}
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  className="user-detail-play-btn"
                                  aria-label={availability.isUnavailable ? `${valueOrNA(item.title)} is unavailable` : `Play ${valueOrNA(item.title)}`}
                                  disabled={availability.isUnavailable}
                                  onClick={() =>
                                    onPlayTrack?.(item, detailDialog.data.tracks || [], index)}
                                >
                                  <Play size={13} />
                                </button>
                              </div>
                            );
                          })
                        ) : (
                          renderEmptyState("tracks")
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}

                {userDetailTab === "playlists" ? (
                  <div className="user-related-sections">
                    <div>
                      <p className="user-detail-label">User Playlists</p>
                      <div className="user-detail-list">
                        {(detailDialog.data.playlists || []).length ? (
                          detailDialog.data.playlists.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              className="user-detail-list-item"
                              onClick={() => openPlaylistOrAlbumDetails(item, "playlist")}
                            >
                              <span>{valueOrNA(item.title)}</span>
                              <span>{formatDurationValue(item.duration)}</span>
                            </button>
                          ))
                        ) : (
                          renderEmptyState("playlists")
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}

                {userDetailTab === "albums" ? (
                  <div className="user-related-sections">
                    <div>
                      <p className="user-detail-label">User Albums</p>
                      <div className="user-detail-list">
                        {(detailDialog.data.albums || []).length ? (
                          detailDialog.data.albums.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              className="user-detail-list-item"
                              onClick={() => openPlaylistOrAlbumDetails(item, "album")}
                            >
                              <span>{valueOrNA(item.title)}</span>
                              <span>{formatDurationValue(item.duration)}</span>
                            </button>
                          ))
                        ) : (
                          renderEmptyState("albums")
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}

            {detailDialog.status === "ready" && detailDialog.type === "track" ? (
              <div className="entity-detail-wrap">
                <h3>{valueOrNA(detailDialog.data.title)}</h3>
                <div className="entity-detail-actions">
                  <button
                    type="button"
                    className="detail-play-btn"
                    disabled={getTrackAvailability(detailDialog.data).isUnavailable}
                    onClick={() =>
                      onPlayTrack?.(detailDialog.data, [detailDialog.data], 0)}
                  >
                    <Play size={15} />
                    {getTrackAvailability(detailDialog.data).isUnavailable
                      ? "Not available in your country"
                      : "Play track"}
                  </button>
                </div>
                <div className="user-detail-grid">
                  <p><strong>Artist:</strong> {valueOrNA(detailDialog.data?.user?.username)}</p>
                  <p><strong>Duration:</strong> {formatDurationValue(detailDialog.data.duration)}</p>
                  <p><strong>Genre:</strong> {valueOrNA(detailDialog.data.genre)}</p>
                  <p><strong>Plays:</strong> {formatCount(detailDialog.data.playback_count)}</p>
                  <p><strong>Likes:</strong> {valueOrNA(detailDialog.data.likes_count)}</p>
                  <p><strong>Reposts:</strong> {valueOrNA(detailDialog.data.reposts_count)}</p>
                  <p><strong>Comment count:</strong> {valueOrNA(detailDialog.data.comment_count)}</p>
                  <p><strong>Policy:</strong> {valueOrNA(detailDialog.data.policy)}</p>
                </div>
                <div className="user-detail-bio">
                  <p className="user-detail-label">Description</p>
                  <p>{valueOrNA(detailDialog.data.description)}</p>
                </div>
              </div>
            ) : null}

            {detailDialog.status === "ready" &&
              (detailDialog.type === "playlist" || detailDialog.type === "album") ? (
              <div className="entity-detail-wrap">
                <h3>{valueOrNA(detailDialog.data.playlist?.title)}</h3>
                <div className="user-detail-grid">
                  <p><strong>Type:</strong> {detailDialog.type === "album" ? "Album" : "Playlist"}</p>
                  <p><strong>Creator:</strong> {valueOrNA(detailDialog.data.playlist?.user?.username)}</p>
                  <p><strong>Track count:</strong> {valueOrNA(detailDialog.data.playlist?.track_count)}</p>
                  <p><strong>Last modified:</strong> {valueOrNA(detailDialog.data.playlist?.last_modified)}</p>
                </div>
                <div className="user-detail-bio">
                  <p className="user-detail-label">Description</p>
                  <p>{valueOrNA(detailDialog.data.playlist?.description)}</p>
                </div>

                <p className="user-detail-label user-detail-section-gap">Tracks</p>
                <div className="user-detail-list">
                  {(detailDialog.data.playlist?.tracks || []).slice(0, 12).map((track, index) => {
                    const availability = getTrackAvailability(track);
                    return (
                      <div
                        key={track.id}
                        className={`user-detail-list-item${availability.isUnavailable ? " is-unavailable" : ""}`}
                      >
                        <button
                          type="button"
                          className="user-detail-main-btn"
                          onClick={() => openTrackDetails(track)}
                        >
                          <span>{valueOrNA(track.title)}</span>
                          <span className={availability.isUnavailable ? "user-detail-track-warning" : ""}>
                            {availability.isUnavailable
                              ? availability.text
                              : formatDurationValue(track.duration)}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="user-detail-play-btn"
                          aria-label={availability.isUnavailable ? `${valueOrNA(track.title)} is unavailable` : `Play ${valueOrNA(track.title)}`}
                          disabled={availability.isUnavailable}
                          onClick={() =>
                            onPlayTrack?.(
                              track,
                              detailDialog.data.playlist?.tracks || [],
                              index
                            )}
                        >
                          <Play size={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </article>
        </div>
      ) : null}
    </>
  );
}

export default HomeView;
